import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import ServicePackageService, {
  IUpdateServicePackageParams,
} from '@clickersphere/core/services/ServicePackageService';
import useToast from '../useToast';
import { useBuildQueryKey } from '../useBuildQueryKey';
import {
  ServicePackageQueryData,
  SERVICE_PACKAGES_QUERY_KEY,
} from './useServicePackages';

export function useUpdateServicePackage() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();
  const { toast } = useToast();
  const { t } = useTranslation();

  const {
    isPending: updateServicePackageIsPending,
    mutateAsync: updateServicePackageAsync,
  } = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: IUpdateServicePackageParams;
    }) => ServicePackageService.update(id, data),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(SERVICE_PACKAGES_QUERY_KEY),
      });
      await queryClient.cancelQueries({
        queryKey: buildQueryKey([SERVICE_PACKAGES_QUERY_KEY, variables.id]),
      });

      const oldServicePackages = queryClient.getQueryData<
        ServicePackageQueryData[]
      >(buildQueryKey(SERVICE_PACKAGES_QUERY_KEY));
      const oldServicePackage =
        queryClient.getQueryData<ServicePackageQueryData>(
          buildQueryKey([SERVICE_PACKAGES_QUERY_KEY, variables.id]),
        );

      const dataWithoutImageAndServiceIds: Omit<
        IUpdateServicePackageParams,
        'imageUrl' | 'serviceIds'
      > = {
        name: variables.data.name,
        description: variables.data.description,
        price: variables.data.price,
      };

      queryClient.setQueryData<ServicePackageQueryData[]>(
        buildQueryKey(SERVICE_PACKAGES_QUERY_KEY),
        (old) =>
          old?.map((item) =>
            item.id === variables.id
              ? {
                  ...item,
                  ...dataWithoutImageAndServiceIds,
                  queryStatus: 'pending',
                }
              : item,
          ),
      );

      queryClient.setQueryData<ServicePackageQueryData>(
        buildQueryKey([SERVICE_PACKAGES_QUERY_KEY, variables.id]),
        (old) =>
          old?.id === variables.id
            ? {
                ...old,
                ...dataWithoutImageAndServiceIds,
                queryStatus: 'pending',
              }
            : old,
      );

      return { oldServicePackages, oldServicePackage };
    },
    onSuccess: async (_data, variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(SERVICE_PACKAGES_QUERY_KEY),
      });

      queryClient.setQueryData<ServicePackageQueryData[]>(
        buildQueryKey(SERVICE_PACKAGES_QUERY_KEY),
        (old) =>
          old?.map((item) =>
            item.id === variables.id
              ? { ...item, queryStatus: undefined }
              : item,
          ),
      );

      queryClient.setQueryData<ServicePackageQueryData>(
        buildQueryKey([SERVICE_PACKAGES_QUERY_KEY, variables.id]),
        (old) =>
          old?.id === variables.id ? { ...old, queryStatus: undefined } : old,
      );

      if (
        variables.data.imageUrl !== undefined ||
        variables.data.serviceIds !== undefined
      ) {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey(SERVICE_PACKAGES_QUERY_KEY),
        });
      }

      toast(t('catalog.toasts.packageUpdated'), 'success');
    },
    onError: async (_error, variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(SERVICE_PACKAGES_QUERY_KEY),
      });
      await queryClient.cancelQueries({
        queryKey: buildQueryKey([SERVICE_PACKAGES_QUERY_KEY, variables.id]),
      });

      queryClient.setQueryData(
        buildQueryKey(SERVICE_PACKAGES_QUERY_KEY),
        context?.oldServicePackages,
      );
      queryClient.setQueryData(
        buildQueryKey([SERVICE_PACKAGES_QUERY_KEY, variables.id]),
        context?.oldServicePackage,
      );

      toast(t('catalog.toasts.packageUpdateError'), 'error');
    },
  });

  return {
    updateServicePackageAsync,
    updateServicePackageIsPending,
  };
}
